import React, {useState, useEffect} from 'react'
import {useSelector} from "react-redux";
import {
    getSentences,
    getTranscriptText,
    getRecordings,
    getMeetingRecordingUrl,
    getVtt
} from "../api/client";
import {Section} from "./Section";
import {IconCopy, Tag} from "@seismic/mantle";
import {SeismicEmployee, Customer} from "./Utils";
import {CenteredSpinner} from "./CenteredSpinner";

export const MeetingTranscriptTab = () => {

    const [recordings, setRecordings] = useState();
    const [transcriptCopied, setTranscriptCopied] = useState(false);
    const [sentenceSummary, setSentenceSummary] = useState();
    const [filter, setFilter] = useState();
    const [selectedSentenceIds, setSelectedSentenceIds] = useState();
    const selectedMeetingId = useSelector(state => state.users.selectedMeetingId);
    
    const loadRecordings = async () => {
        let recordings = await getRecordings(selectedMeetingId);
        setRecordings(recordings);
    }
    
    const copyTranscriptToClipboard = async () => {
        let transcriptText = await getTranscriptText(selectedMeetingId);
        navigator.clipboard.writeText(transcriptText);
        setTranscriptCopied(true);
    }
    
    const loadSentenceSummary = async () => {
        let summary = await getSentences(selectedMeetingId);
        setSentenceSummary(summary);
    }
    
    const selectSentence = (x) => {
        if(selectedSentenceIds == null || !selectedSentenceIds.startsWith(x.id)) {
            let idList = x.id + "|";
            let index = sentenceSummary.sentences.findIndex(z=>z.id === x.id );
            var start = index - 5;
            if(start < 0)
                start = 0;
            var end = index + 5;
            if(end > sentenceSummary.sentences.length)
                end = sentenceSummary.sentences.length
            for(var i = start; i < end; i++){
                idList += sentenceSummary.sentences[i].id + "|";
            }
            setSelectedSentenceIds(idList); //select existing with some on either side
        }
        else {
            setSelectedSentenceIds(null); //unselect existing
        }
    }
    
    const filterChanged = (topic) => {
        if(filter === topic) {
            setFilter(null);
        }
        else {
            setFilter(topic);
        }
    }

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes'

        const k = 1024
        const dm = decimals < 0 ? 0 : decimals
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

        const i = Math.floor(Math.log(bytes) / Math.log(k))

        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
    }
    
    const downloadRecording = async (e, recording) => {
        e.preventDefault();
        var data = await getMeetingRecordingUrl(selectedMeetingId, recording.id)
        window.open(data.url);
    }

    const downloadText = async (e) => {
        e.preventDefault();
        let transcriptText = await getTranscriptText(selectedMeetingId);
        let blob = new Blob([transcriptText], {type: "text/plain"});
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement("a");
        document.body.appendChild(a);
        a.style = "display: none";
        a.href = url;
        a.download = `${selectedMeetingId}.txt`;
        a.click();
        window.URL.revokeObjectURL(url);
    }
    
    const downloadVtt = async (e) => {
        e.preventDefault();
        let vttData = await getVtt(selectedMeetingId);
        let blob = new Blob([vttData], {type: "octet/stream"});
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement("a");
        document.body.appendChild(a);
        a.style = "display: none";
        a.href = url;
        a.download = `${selectedMeetingId}.vtt`;
        a.click();
        window.URL.revokeObjectURL(url);
    }
    
    useEffect(() => {
        setRecordings(null);
        setSentenceSummary(null);
        setFilter(null);
        setTranscriptCopied(false);
        if(selectedMeetingId != null && selectedMeetingId !== "") {
            loadSentenceSummary();
            loadRecordings();
        }
    }, [selectedMeetingId]);
    
    if(selectedMeetingId == null)
        return <div>No meeting selected.</div>
    
    if(sentenceSummary == null)
        return <CenteredSpinner height="80vh" message="Loading..." />
    
    return <div>
        
        <div className="header">Downloads</div>
        <div className="meetingPanel">
            {recordings == null ? "N/A" : recordings.map((a, b)=><>{b !== 0 ? " | " : ""}<a className="downloadLink" href="" onClick={(e) => downloadRecording(e, a)}>{a.recordingType} ({formatBytes(a.size)})</a></>)}
            | <a className="downloadLink" href="" onClick={downloadVtt}>VTT (&lt; 1 MB)</a> | <a className="downloadLink" href="" onClick={downloadText}>Transcript Text (&lt; 1 MB)</a>
        </div>

        <div className="header">Topics</div>
        <div className="meetingPanel">
            {sentenceSummary.topics.map(x=> <span style={{cursor:"pointer"}} key={x.topic}><Tag onClick={()=>filterChanged(x.topic)} style={{backgroundColor: filter === x.topic ? "#d1e8fd" : "#f3f4f6"}}>{x.topic}&nbsp;<span>({x.count})</span></Tag>&nbsp;</span>)}
        </div>
        
        <div className="header">Transcript <IconCopy size={16} onClick={copyTranscriptToClipboard} />{transcriptCopied ? <span className="text-success">Transcript Copied</span> : <></>}</div>
        <div className="meetingPanel" style={{height: "60vh", overflow: "auto"}}>
            <Section headers={['speaker', 'transcript', 'topics']} width={50}>
                {sentenceSummary.sentences.filter(x => 
                    (selectedSentenceIds == null && (filter == null || x.topics?.some(z=>z === filter)) || selectedSentenceIds != null && selectedSentenceIds.indexOf(x.id) >= 0)).map(x=>
                    <tr style={{cursor:"pointer"}} key={x.id} onClick={() => selectSentence(x)} className={selectedSentenceIds != null && selectedSentenceIds.startsWith(x.id) ? 'bg-info bg-opacity-25' : ''}>
                        <td className="text-nowrap">{x.isCustomer ? <Customer/> : <SeismicEmployee />}&nbsp;{x.participantName}</td>
                        <td>{x.sentenceText}</td>
                        <td>{x.topics?.map(t=><Tag key={x.id + '_' + t} variant="info">{t}</Tag>)}</td>
                    </tr>)}
            </Section>
        </div>

    </div>

};